import React, { useState, useEffect } from 'react';

import { useGetAccountInfo, useGetNetworkConfig, useGetPendingTransactions } from '@multiversx/sdk-dapp/hooks';
import { Account, Address, BigUIntValue, ContractFunction, Interaction, SmartContract, TokenPayment, U32Value } from '@multiversx/sdk-core/out';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Checkbox, checkboxClasses, FormControlLabel } from '@mui/material';
import axios from 'axios';
import BigNumber from 'bignumber.js/bignumber';
import clsx from 'clsx';
import { Spinner } from 'react-bootstrap';
import Form from 'react-bootstrap/Form';
import { useMediaQuery } from 'react-responsive';
import { useNavigate, createSearchParams } from 'react-router-dom';

import { CustomButton, CustomModal, SwapTabs } from 'components';
import { contracts, ElrondApiUrl, isDev } from 'config/config';

import { BigZero, coinNames, coins } from 'helpers/constant';
import { convertEsdtToWei, convertWeiToEsdt, getSymbol, getTokenImg, handleTokenImgError, shortenEsdt, UnwrapToken } from 'helpers/utils';
import { useInteraction } from 'Interaction';
import styles from './styles.module.scss';
import { CustomInput } from 'components/CustomInput';
import { toast } from 'react-toastify';
import { routeNames } from 'routes';
import { ProxyNetworkProvider } from '@multiversx/sdk-network-providers/out';
import { sendTransactions } from '@multiversx/sdk-dapp/services';

export const Dashboard = () => {
  const [wegldBalance, setWegldBalance] = useState('');
  const [searchKey, setSearchKey] = useState('');

  const [onlyWalletAssets, setOnlyWalletAssets] = useState(false);
  const [onlyLockedAssets, setOnlyLockedAssets] = useState(false);

  const [walletAssets, setWalletAssets] = useState<any[]>([]);
  const [lockedAssets, setLockedAssets] = useState<any[]>([]);

  const [showUnwrapModal, setShowUnwrapModal] = useState(false);
  const [wrapAmount, setWrapAmount] = useState(BigZero);
  const [showWrapModal, setShowWrapModal] = useState(false);
  const [unwrapAmount, setUnwrapAmount] = useState(BigZero);

  const { fetching, farmInfos, pairInfos, callMethod } = useInteraction();
  const { account: {address: userAddress, balance: userBalance, shard} } = useGetAccountInfo();
  const { network: { apiAddress } } = useGetNetworkConfig();
  const networkProvider = new ProxyNetworkProvider(apiAddress, { timeout: 10000 });
  const { hasPendingTransactions } = useGetPendingTransactions();

  const navigate = useNavigate();
  const isMobile = useMediaQuery({
    query: '(max-width: 685px)'
  });

  useEffect(() => {
    if(!userAddress) {
      navigate(routeNames.swap);
    }

    (async () => {
      try {
        const { data } = await axios.get(`${ElrondApiUrl}/accounts/${userAddress}/tokens?size=1000`);
  
        const assets = [];

        assets.push({
          balance: userBalance,
          identifier: 'EGLD',
          ticker: 'EGLD',
          decimals: 18
        });
  
        for(const token of data) {
          if(token.type == 'FungibleESDT') {
            assets.push(token);
          }
        }
  
        setWalletAssets(assets);
      } catch (error) {
        console.log(error);
      }
    })();
  }, [userAddress, userBalance, hasPendingTransactions]);

  useEffect(() => {
    const timer = setTimeout(async () => {
      findValidPools();
    }, 500);

    return () => clearTimeout(timer);
  }, [searchKey]);

  const findValidPools = () => {
    (async () => {
      const { data } = await axios.get(`${ElrondApiUrl}/accounts/${userAddress}/tokens?size=1000`);
    
      const assets = [];

      assets.push({
        balance: userBalance,
        identifier: 'EGLD',
        ticker: 'EGLD',
        decimals: 18
      });

      for(const token of data) {
        if(searchKey) {
          if(
            !token.identifier.toLowerCase().includes(searchKey.toLowerCase())
          ) continue;
        }

        if(token.type == 'FungibleESDT') {
          assets.push(token);
        }
      }

      setWalletAssets(assets);
    })();
  };

  const wrap = async () => {
    try {
      const transactions = [];

      if(BigZero.lt(wrapAmount)) {
          let wrapAddress = new Address(contracts.Wrap0.address);
          
          if(shard === 1) {
              wrapAddress = new Address(contracts.Wrap1.address);
          } else if (shard === 2) {
              wrapAddress = new Address(contracts.Wrap2.address);
          }
          const wrapContract = new SmartContract({ address: wrapAddress });
          const wrapFunction = new ContractFunction('wrapEgld');
          const wrapInteraction = new Interaction(wrapContract, wrapFunction, []);

          const userAccount = new Account(new Address(userAddress));

          const updatedUserAccount = await networkProvider.getAccount(
              new Address(userAddress)
          );

          userAccount.update(updatedUserAccount);

          const wrapTx = wrapInteraction
              .withNonce(userAccount.nonce)
              .withValue(TokenPayment.egldFromAmount(wrapAmount))
              .withGasLimit(20000000)
              .withChainID(isDev ? 'D' : '1')
              .buildTransaction();

          transactions.push(wrapTx);

          await sendTransactions({
            transactions: transactions,
            transactionsDisplayInfo: {
              processingMessage: 'Processing Transaction',
              errorMessage: 'Transaction Failed',
              successMessage: 'Transaction Success',
            },
            redirectAfterSign: false,
          });
      }

      setShowWrapModal(false);
  } catch(error: any) {
      console.log(error);
  }
  };

  const unwrap = async () => {
    try {
      const transactions = [];

      if(BigZero.lt(unwrapAmount)) {
          let wrapAddress = new Address(contracts.Wrap0.address);
          
          if(shard === 1) {
              wrapAddress = new Address(contracts.Wrap1.address);
          } else if (shard === 2) {
              wrapAddress = new Address(contracts.Wrap2.address);
          }
          const wrapContract = new SmartContract({ address: wrapAddress });
          const wrapFunction = new ContractFunction('unwrapEgld');
          const wrapInteraction = new Interaction(wrapContract, wrapFunction, []);

          const userAccount = new Account(new Address(userAddress));

          const updatedUserAccount = await networkProvider.getAccount(
              new Address(userAddress)
          );

          userAccount.update(updatedUserAccount);

          const wrapTx = wrapInteraction
              .withNonce(userAccount.nonce)
              .withSingleESDTTransfer(TokenPayment.fungibleFromAmount(coinNames[1], unwrapAmount, 18))
              .withGasLimit(20000000)
              .withChainID(isDev ? 'D' : '1')
              .buildTransaction();

          transactions.push(wrapTx);

          await sendTransactions({
            transactions: transactions,
            transactionsDisplayInfo: {
              processingMessage: 'Processing Transaction',
              errorMessage: 'Transaction Failed',
              successMessage: 'Transaction Success',
            },
            redirectAfterSign: false,
          });
      }

      setShowUnwrapModal(false);
  } catch(error: any) {
      console.log(error);
  }
  };

  return (
    <>
      <div className='d-flex flex-column flex-fill align-items-center justify-content-start'>
        <div className={clsx(styles.card, 'card shadow-sm')}>
          <div className={styles.cardBody}>
            <div className='d-flex justify-content-start w-100' style={{ fontSize: 24, color: 'white', paddingLeft: 11.5 }}>
              Your Assets
            </div>

            {/* <div className='d-flex w-100 justify-content-end text-white'>
                <FormControlLabel
                  control={
                    <Checkbox
                      sx={{
                        color: 'white',
                        [`&.${checkboxClasses.checked}`]: {
                          color: '#ff9d19',
                        },
                      }}
                      checked={onlyWalletAssets}
                      onChange={(e, checked) => setOnlyWalletAssets(checked)}
                    />
                  }
                  label="Wallet Assets"
                />

                <FormControlLabel
                  className='ml-2'
                  control={
                    <Checkbox
                      sx={{
                        color: 'white',
                        [`&.${checkboxClasses.checked}`]: {
                          color: '#ff9d19',
                        },
                      }}
                      checked={onlyLockedAssets}
                      onChange={(e, checked) => setOnlyLockedAssets(checked)}
                    />
                  }
                  label="Locked Assets"
                />
            </div> */}
              <div className='w-100 d-flex justify-content-start align-items-center'>
                <input
                    className={clsx(styles.search, 'flex-grow-1 my-4 mr-2')}
                    type='text'
                    placeholder='Search Pool'
                    value={searchKey}
                    onChange={(e) => setSearchKey(e.target.value)}
                />
              </div>
              {
                !isMobile && (
                  <div className={clsx(styles.listHeader, 'd-flex justify-content-between align-items-center row')}>
                    <div className='col-1'>#</div>
                    <div className='col-6'>Asset</div>
                    <div className='col-5 text-center'>Balance</div>
                  </div>
                )
              }

              <div className={styles.list}>
                {
                  walletAssets.length > 0 &&
                    walletAssets.map((asset, index) => {
                      return (
                        <Accordion
                          key={index}
                          className={clsx(styles.listItem)}
                        >
                          <AccordionSummary
                            sx={{ px: 0 }}
                            expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
                            aria-controls="panel1a-content"
                            id="panel1a-header"
                          >
                            {
                              !isMobile ? (
                                <div className='col-sm-1'>
                                  {index + 1}
                                </div>
                              ) : (
                                <></>
                              )
                            }
                            <div className='col-6 col-sm-6 d-flex align-items-center'>
                              <img src={getTokenImg(asset?.identifier)} onError={handleTokenImgError}  alt='' width={24} />
                              <span className='bold ml-2'>{asset?.ticker}</span>
                            </div>
                            <div className='col-6 col-sm-5 d-flex align-items-center justify-content-center'>
                              {shortenEsdt(convertWeiToEsdt(asset.balance, asset.decimals))}
                            </div>
                          </AccordionSummary>
                          <AccordionDetails className='row justify-content-center'>
                            {
                              asset?.identifier == coinNames[0] && (
                                <div className='col-4 text-center mt-4 p-0'>
                                  <CustomButton
                                    className={styles.button}
                                    onClick={() => {
                                      if(userAddress) {
                                        setShowWrapModal(true);
                                      }
                                    }}
                                  >
                                    Wrap
                                  </CustomButton>
                                </div>
                              )
                            }

                            {
                              asset?.identifier == coinNames[1] && (
                                <div className='col-6 text-center mt-4 p-0'>
                                  <CustomButton
                                    className={styles.button}
                                    onClick={() => {
                                      if(userAddress) {
                                        setWegldBalance(asset?.balance);
                                        setShowUnwrapModal(true);
                                      }
                                    }}
                                  >
                                    Unwrap
                                  </CustomButton>
                                </div>
                              )
                            }
                            

                            {/* <div className='col-4 text-center mt-4 p-0'>
                              <CustomButton
                                className={styles.button}
                                onClick={() => {
                                  if(userAddress) {
                                  }
                                }}
                              >
                                Unstake
                              </CustomButton>
                            </div> */}

                            <div className='col-6 text-center mt-4 p-0'>
                              <CustomButton
                                className={styles.button}
                                onClick={() => {
                                  if(!userAddress) {
                                    return;
                                  }

                                  navigate(`/swap?firstToken=${asset?.identifier}`);
                                }}
                              >
                                Swap
                              </CustomButton>
                            </div>
                          </AccordionDetails>
                        </Accordion>
                      );
                    })
                  }
              </div>
          </div>
        </div>
      </div>

      <CustomModal
        title='Wrap'
        show={showWrapModal}
        onClose={() => setShowWrapModal(false)}
      >
        <div className='d-flex flex-column justify-content-center align-items-start'>
          <h6 className='text-white text-left mt-4'>EGLD Wrap Amount</h6>
          <span className='text-info'>EGLD</span>
          <span className='text-white'>Balance: {shortenEsdt(convertWeiToEsdt(userBalance))}</span>
          <CustomInput
              className={clsx(styles.import, 'flex-grow-1 w-100')}
              placeholder='Wrap Amount'
              value={wrapAmount}
              maxValue={new BigNumber(userBalance)}
              setAmount={(value: BigNumber) => {
                if(value.lte(new BigNumber(userBalance))) {
                  setWrapAmount(value);
                }
              }}
          />
          <CustomButton
              className={styles.applyButton}
              onClick={() => wrap()}
          >
              Wrap
          </CustomButton>
        </div>
      </CustomModal>

      <CustomModal
        title='Unwrap'
        show={showUnwrapModal}
        onClose={() => setShowUnwrapModal(false)}
      >
        <div className='d-flex flex-column justify-content-center align-items-start'>
          <h6 className='text-white text-left mt-4'>WEGLD Unwrap Amount</h6>
          <span className='text-info'>{coinNames[1]}</span>
          <span className='text-white'>Balance: {shortenEsdt(convertWeiToEsdt(wegldBalance))}</span>
          <CustomInput
              className={clsx(styles.import, 'flex-grow-1 w-100')}
              placeholder='Unwrap Amount'
              value={unwrapAmount}
              maxValue={convertWeiToEsdt(wegldBalance)}
              setAmount={(value: BigNumber) => {
                if(value.lte(new BigNumber(wegldBalance))) {
                  setUnwrapAmount(value);
                }
              }}
          />
          <CustomButton
              className={styles.applyButton}
              onClick={() => unwrap()}
          >
              Unwrap
          </CustomButton>
        </div>
      </CustomModal>
    </>
  );
};
